import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import RichTextEditor from '../components/RichTextEditor';
import {api} from '../../api';

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', department: '', location: '', employmentType: 'full_time', description: '', salaryMin: '', salaryMax: '' });

  const load = () => api('/ats/jobs').then(r => r.ok ? r.json() : []).then(setJobs).catch(() => {});
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: '', department: '', location: '', employmentType: 'full_time', description: '', salaryMin: '', salaryMax: '' }); setEditing(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await api(`/ats/jobs/${editing}`, { method: 'PUT', body: JSON.stringify(form) });
    } else {
      await api('/ats/jobs', { method: 'POST', body: JSON.stringify(form) });
    }
    resetForm();
    load();
  };

  const handleEdit = (job) => {
    setForm({ title: job.title, department: job.department || '', location: job.location || '', employmentType: job.employmentType || 'full_time', description: job.description || '', salaryMin: job.salaryMin ?? '', salaryMax: job.salaryMax ?? '' });
    setEditing(job.id);
    setShowForm(true);
  };

  const handleToggleStatus = async (job) => {
    await api(`/ats/jobs/${job.id}`, { method: 'PUT', body: JSON.stringify({ ...job, status: job.status === 'open' ? 'closed' : 'open' }) });
    load();
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Job Postings</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">+ New Job</button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{editing ? 'Edit Job' : 'New Job Posting'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                <select value={form.employmentType} onChange={e => setForm({ ...form, employmentType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600">
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="temporary">Temporary</option>
                  <option value="internship">Internship</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <RichTextEditor value={form.description} onChange={val => setForm({ ...form, description: val })} placeholder="Job description..." />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Min</label>
                <input type="number" step="0.01" value={form.salaryMin} onChange={e => setForm({ ...form, salaryMin: e.target.value ? parseFloat(e.target.value) : '' })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" placeholder="e.g. 50000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Max</label>
                <input type="number" step="0.01" value={form.salaryMax} onChange={e => setForm({ ...form, salaryMax: e.target.value ? parseFloat(e.target.value) : '' })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" placeholder="e.g. 80000" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">{editing ? 'Save' : 'Create'}</button>
              <button type="button" onClick={resetForm} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicants</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {jobs.map(job => (
              <tr key={job.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <Link to={`/ats/jobs/${job.id}`} className="text-teal-600 hover:text-teal-800 font-medium">{job.title}</Link>
                </td>
                <td className="px-6 py-4 text-gray-600">{job.department || '—'}</td>
                <td className="px-6 py-4 text-gray-600">{job.location || '—'}</td>
                <td className="px-6 py-4 text-gray-600 text-sm">{(job.employmentType || '').replace('_', ' ')}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${job.status === 'open' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{job.status}</span>
                </td>
                <td className="px-6 py-4 text-gray-600">{job.applicantCount ?? 0}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => handleEdit(job)} className="text-sm text-gray-600 hover:text-teal-600">Edit</button>
                  <button onClick={() => handleToggleStatus(job)} className="text-sm text-gray-600 hover:text-teal-600">{job.status === 'open' ? 'Close' : 'Reopen'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {jobs.length === 0 && <div className="text-center py-12 text-gray-500">No job postings yet.</div>}
      </div>
    </Layout>
  );
}
